import { ColDef, ColGroupDef, RowNode } from '@ag-grid-community/core';
import { Injectable, OnDestroy } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { HeaderRendererComponent } from '@frk/eds-components';
import query from '@graphql/morningstar-table/morningstar-table.graphql';
import { FundListing, Product, Ratings, ShareClass } from '@models';
import { PpssCard, PpssCardItem, PpssCardSection } from '@types';
import { AgGridHelperService } from '@products/fund-listing/services/ag-grid-helper.service';
import { GraphQLFundDataService } from '@products/services/graphql-fund-data.service';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import { getFirstNonEmptyProp } from '@products/utils/mappers/mapper.utils';
import { ProductsMapper } from '@products/utils/mappers/product.type.mapper';
import { MapperParams } from '@products/utils/mappers/type.mapper';
import { SiteConfigService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import { FundShareClassId, ProductData, ProductDTO } from '@types';
import { Logger } from '@utils/logger';
import groupBy from 'lodash/groupBy';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

const logger = Logger.getLogger('MorningstarTableService');

// custom sorting function for rating columns
// initial sort by rating, and secondary sort alphabetically by fund name
export const ratingsSort = (
  ratingA: number,
  ratingB: number,
  rowA: RowNode,
  rowB: RowNode
): number => {
  const ratingsDiff: number = ratingA - ratingB;
  if (ratingsDiff !== 0) {
    // sort based on rating
    return ratingsDiff;
  }
  // ratings are equal, so secondary sort on fund name
  const fundNameA: string = rowA.data.fundName;
  const fundNameB: string = rowB.data.fundName;
  return fundNameB.localeCompare(fundNameA);
};

interface AllProductTypesDTO {
  cef: ProductDTO[];
  etf: ProductDTO[];
  money: ProductDTO[];
  mutual: ProductDTO[];
  vip: ProductDTO[];
  _529: ProductDTO[];
  interval: ProductDTO[];
}

type MorningstarCategoryDetails = Pick<
  Ratings,
  | 'morningStarCategory'
  | 'asOfDate'
  | 'monthEndNoOfFunds3yr'
  | 'monthEndNoOfFunds5yr'
  | 'monthEndNoOfFunds10yr'
>;

export interface GridData {
  colDefs: ColGroupDef[];
  rowData: ProductData[];
  cards: PpssCard[];
  morningStarCategory?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MorningstarTableService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();
  private mapper: ProductsMapper;
  private fundListingSubject$: ReplaySubject<FundListing> = new ReplaySubject<FundListing>(
    1
  );
  private isSiteIntl: boolean;

  /*
    the ratings data will be for all funds, so only needs requested once and then cached
    After data is first requested, this is set to true so data isn't requested again
  */
  private dataRequested = false;

  constructor(
    private fundDataService: GraphQLFundDataService,
    private agGridHelperService: AgGridHelperService,
    private siteConfigService: SiteConfigService,
    private mapperFactory: MapperFactory,
    private translateService: TranslateService
  ) {
    this.isSiteIntl = this.siteConfigService.isSiteInternational();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * returns the default column definition for MorningStar tables
   */
  public getGridDefaultColDef(): ColDef {
    return {
      headerComponentFramework: HeaderRendererComponent,
      sortable: true,
      flex: 1,
      wrapText: true,
      autoHeight: true,
    };
  }

  /**
   * returns ratings data for a specific list of funds (share classes)
   * The data contains both column definitions and row data for use in ag grid
   * The data is grouped into several tables based on the Morningstar Category
   * @param shareClasses total list of funds to get tables for
   * @returns array of GridData objects
   */
  public getGridData$(
    shareClasses: FundShareClassId[]
  ): Observable<GridData[]> {
    logger.debug('getGridRowData$()', shareClasses);

    // trigger ratings gql retreival if data not requested yet
    if (!this.dataRequested) {
      this.requestData();
    }

    return this.fundListingSubject$.pipe(
      map(this.processListing(shareClasses)),
      map((rowData: ProductData[]): GridData[] =>
        Object.values(
          // group by morningstar category
          groupBy(rowData, 'shareClass.ratings.morningStarCategory')
        )
          // map into GridData objects
          .map(
            (categoryRows: ProductData[]): GridData => {
              // rows are sorted by overall rating initially
              const rows: ProductData[] = categoryRows.sort(
                (a: ProductData, b: ProductData): number =>
                  b.shareClass?.ratings?.morningStarRatingOverall -
                  a.shareClass?.ratings?.morningStarRatingOverall
              );
              const category: MorningstarCategoryDetails = this.getCategoryDetails(
                rows
              );
              const colDefs: ColGroupDef[] = this.getColDefs(category);
              const cards: PpssCard[] = rows.map(this.mapCard(colDefs));
              return {
                morningStarCategory: `${this.translateService.instant(
                  'products.morningstar_category'
                )} ${categoryRows[0].shareClass?.ratings?.morningStarCategory}`,
                rowData: rows,
                colDefs,
                cards,
              };
            }
          )
          // sort grids alphabetically on morningstar category
          .sort((a: GridData, b: GridData): number =>
            a.rowData[0].shareClass?.ratings?.morningStarCategory.localeCompare(
              b.rowData[0].shareClass?.ratings?.morningStarCategory
            )
          )
      )
    );
  }

  /**
   * @param row used to work out category specific definitions such as the title and number of funds in category
   * @returns the column definitions for a specific MorningStar Category table
   */
  private getColDefs = (
    category: MorningstarCategoryDetails
  ): ColGroupDef[] => [
    {
      groupId: 'FUND',
      headerClass: 'ag-header-group-cell--align-left',
      headerGroupComponent: 'fundNameGroupHeaderComponent',
      headerGroupComponentParams: {
        title: `${this.translateService.instant(
          'products.morningstar_category'
        )} ${category.morningStarCategory}`,
      },
      children: [
        this.agGridHelperService.getFundNameColDef(this.isSiteIntl, false, {
          cellRendererParams: { showSecondSuffix: true },
          headerName: this.translateService.instant(
            'products.morningstar_fund-name-col-header'
          ),
        }),
        this.agGridHelperService.getMorningStarOverallRatingColDef(
          this.isSiteIntl,
          {
            cellRendererParams: {
              hideNumberOfFunds: true,
              showStarLabels: true,
              theme: 'ratings',
            },
            comparator: ratingsSort,
            headerComponentParams: {
              isAlignRight: false,
              footnotePlacement: 'Fund Morningstar:RatingsOverall',
            },
            minWidth: 155,
            maxWidth: 180,
            // default sort on this column
            sort: 'desc',
            // NB: sortingOrder needs to be implemented in EDS before this will work
            sortingOrder: ['desc', 'asc'],
          }
        ),
      ],
    },
    {
      groupId: 'MORNINGSTARRATINGS',
      children: [
        this.agGridHelperService.getMorningStar3yrRatingColDef({
          cellRendererParams: {
            hideNumberOfFunds: true,
            showStarLabels: true,
            theme: 'ratings',
          },
          comparator: ratingsSort,
          headerComponentParams: {
            labelSmallText: category.monthEndNoOfFunds3yr
              ? `${
                  category.monthEndNoOfFunds3yr
                } ${this.translateService.instant(
                  'products.morningstar_funds-in-category'
                )}`
              : '',
          },
          minWidth: 180,
          maxWidth: 210,
          // NB: sortingOrder needs to be implemented in EDS before this will work
          sortingOrder: ['desc', 'asc'],
        }),
        this.agGridHelperService.getMorningStar5yrRatingColDef({
          cellRendererParams: {
            hideNumberOfFunds: true,
            showStarLabels: true,
            theme: 'ratings',
          },
          comparator: ratingsSort,
          headerComponentParams: {
            labelSmallText: category.monthEndNoOfFunds5yr
              ? `${
                  category.monthEndNoOfFunds5yr
                } ${this.translateService.instant(
                  'products.morningstar_funds-in-category'
                )}`
              : '',
          },
          minWidth: 180,
          maxWidth: 210,
          // NB: sortingOrder needs to be implemented in EDS before this will work
          sortingOrder: ['desc', 'asc'],
        }),
        this.agGridHelperService.getMorningStar10yrRatingColDef({
          cellRendererParams: {
            hideNumberOfFunds: true,
            showStarLabels: true,
            theme: 'ratings',
          },
          comparator: ratingsSort,
          headerComponentParams: {
            labelSmallText: category.monthEndNoOfFunds10yr
              ? `${
                  category.monthEndNoOfFunds10yr
                } ${this.translateService.instant(
                  'products.morningstar_funds-in-category'
                )}`
              : '',
          },
          minWidth: 170,
          maxWidth: 200,
          // NB: sortingOrder needs to be implemented in EDS before this will work
          sortingOrder: ['desc', 'asc'],
        }),
      ],
    },
  ];

  /*
    First, this flattens the array of funds into an array of share classes
    I.e. the array of arrays is flattened into a single array
    Secondly, it filters the share classes to those requested in the component config
  */
  private processListing = (shareClasses: FundShareClassId[]) => (
    listing: FundListing
  ): ProductData[] =>
    listing.products
      // flatten product objects into share class arrays
      .map(this.flattenShareClasses)
      // flatten to a single array of share classes
      .flat()
      // filter on the requested share classes
      .filter((product: ProductData): boolean =>
        shareClasses.includes(product.shareClass?.fundShareClassId)
      );

  /**
   * this requests the ratings data from gql
   * data is for ALL funds, so only needs requested once
   */
  private requestData(): void {
    logger.debug('requestData()');
    this.dataRequested = true;

    const mapperParams: MapperParams = {
      config: this.siteConfigService,
      // configurationName: fundDataServiceParams.configurationName,
    };
    logger.debug('mapperParams', mapperParams);

    this.mapper = this.mapperFactory.createMapper(ProductsMapper, mapperParams);

    this.fundDataService
      .register(query, {
        countrycode: this.fundDataService.getCountry(),
        languagecode: this.fundDataService.getLanguage(),
        productTypemf: 'mf',
        productTypeetf: 'etf',
        productType529: '529',
        productTypecef: 'cef',
        productTypevip: 'variable-insurance',
        productTypemmf: 'mmf',
        productTypeif: 'if',
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        map(this.mergeProductTypes),
        map(this.createParser(this.mapper))
      )
      .subscribe((data: FundListing) => {
        logger.debug('requestData() subscribe response', data);
        this.fundListingSubject$.next(data);
      });
  }

  /**
   * The GQL query returns data grouped by product type
   * This merges all product types into a single DTO array
   */
  private mergeProductTypes = (
    result: ApolloQueryResult<AllProductTypesDTO>
  ): ProductDTO[] =>
    result.data.cef.concat(
      result.data.etf,
      result.data.money,
      result.data.mutual,
      result.data.vip,
      result.data._529,
      result.data.interval
    );

  /*
    This parses the gql data array into a new FundListing object
  */
  private createParser = (mapper: ProductsMapper) => (
    productsDTO: ProductDTO[]
  ): FundListing => {
    // logger.debug('parseProductData()', productsDTO);

    const products: Product[] = mapper.toDomain(productsDTO);
    // logger.debug('products', products);

    return new FundListing(products);
  };

  /*
    takes a fund object and returns a flatened array of it's share classes
  */
  private flattenShareClasses = (product: Product): ProductData[] =>
    product.shareClasses.map(
      (shareclass: ShareClass): ProductData => {
        const { shareClasses, ...productRef } = product;
        const productData: ProductData = productRef;
        productData.shareClass = shareclass;
        return productData;
      }
    );

  /**
   * This maps a ProductData row into a PpssCard data object, for use on the mobile view
   * @param colDefs predefined col defs used for ag grid
   * @returns PpssCard object
   */
  private mapCard = (colDefs: ColGroupDef[]) => (
    row: ProductData
  ): PpssCard => {
    // empty section is used to trigger background highlight on overall rating section
    const sectionEmpty: PpssCardSection = {
      sectionId: `ratings-section-empty_${row.shareClass?.fundShareClassId}`,
      items: [],
    };

    const sectionOverall: PpssCardSection = {
      sectionId: `ratings-section-overall_${row.shareClass?.fundShareClassId}`,
      items: [this.agGridHelperService.processCol(colDefs[0].children[1], row)],
    };

    const items: PpssCardItem[] = [
      // uses the col defs already defined for ag grid
      this.agGridHelperService.processCol(colDefs[1].children[0], row),
      this.agGridHelperService.processCol(colDefs[1].children[1], row),
      this.agGridHelperService.processCol(colDefs[1].children[2], row),
    ];
    const sectionYearly: PpssCardSection = {
      sectionId: `ratings-section-years_${row.shareClass?.fundShareClassId}`,
      items,
    };
    logger.debug('sections:', sectionEmpty, sectionOverall, sectionYearly);
    return {
      cardId: row.shareClass?.fundShareClassId,
      shareClassName: row.shareClass?.shareClassName,
      fundDetail: this.agGridHelperService.getFundDetailToDisplay(
        row,
        this.isSiteIntl,
        true
      ),
      fundLink: row.shareClass?.link,
      sections: [sectionEmpty, sectionOverall, sectionYearly],
      hideFavorite: true,
    };
  };

  private getCategoryDetails(rows: ProductData[]): MorningstarCategoryDetails {
    const ratings: Ratings[] = rows.map(
      (row: ProductData): Ratings => row.shareClass?.ratings
    );
    return {
      morningStarCategory: getFirstNonEmptyProp(ratings, 'morningStarCategory'),
      asOfDate: getFirstNonEmptyProp(ratings, 'asOfDate'),
      monthEndNoOfFunds3yr: getFirstNonEmptyProp(
        ratings,
        'monthEndNoOfFunds3yr'
      ),
      monthEndNoOfFunds5yr: getFirstNonEmptyProp(
        ratings,
        'monthEndNoOfFunds5yr'
      ),
      monthEndNoOfFunds10yr: getFirstNonEmptyProp(
        ratings,
        'monthEndNoOfFunds10yr'
      ),
    };
  }
}
